import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, withPhotoLinks } from "@/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import DialButton from "@/components/DialButton";
import CopyButton from "@/components/CopyButton";
import ImageLightbox from "@/components/ImageLightbox";
import TaskDateFilter, { filterTasksByDate } from "@/components/TaskDateFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Phone, AlertTriangle, Pencil, User2, UserRoundCog, Play, Pause, CheckCircle2, LifeBuoy, CheckSquare, Search, CalendarClock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// The 4 statuses a task actually moves through once it's live. Kept
// separate from COLUMNS below so the "→ move to" quick buttons never offer
// "Scheduled" as a destination — a task only enters that status at creation
// time (via the Schedule Task flow), never by being moved into it later.
const MOVE_TARGETS = [
  { key: "pending", label: "Pending", accent: "bg-slate-100 text-slate-700" },
  { key: "in_progress", label: "In progress", accent: "bg-blue-50 text-[#0055FF]" },
  { key: "on_hold", label: "On hold", accent: "bg-amber-50 text-amber-700" },
  { key: "done", label: "Done", accent: "bg-emerald-50 text-emerald-700" },
];

const COLUMNS = [
  { key: "scheduled", label: "Scheduled", accent: "bg-violet-50 text-violet-700" },
  ...MOVE_TARGETS,
];

// Status filter tabs shown above the board — "all" reveals the full kanban,
// any other value narrows the board down to that single status column.
const STATUS_FILTERS = [...COLUMNS, { key: "all", label: "All" }];

// Compact IST timestamp e.g. "16 Jul, 14:32"
const IST_STAMP = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
      hour12: false,
    });
  } catch {
    return "—";
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // task being edited, or null
  const [reassigning, setReassigning] = useState(null); // { task, to } for the Reassign box
  // Date-range filter — defaults to "Created date" with no range so the
  // full list is visible on first load.
  const [dateFilter, setDateFilter] = useState({ field: "created_at", from: "", to: "" });
  // Board defaults to Pending only; the other statuses render only once the
  // user explicitly switches tabs (or picks "All" for the full kanban).
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  // true while the New-task dialog is open in "Schedule Task" mode — same
  // dialog, just with an extra required date field and different copy.
  const [scheduling, setScheduling] = useState(false);
  // { images: [...], title: '...', startIndex: 0 } — drives the lightbox
  // for ticket photos AND employee proof-of-work photos on completed tasks.
  const [lightbox, setLightbox] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    customer_name: "",
    customer_phone: "",
    customer_phone_2: "",
    assigned_to: "",
    priority: "medium",
    scheduled_date: "",
  });

  // Employees load on their own: the full task list (every task, with its
  // photos) can take a while on mobile data, and the "Assign to" list
  // shouldn't wait for it.
  const loadEmployees = async () => {
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
      if (data[0]) setForm((f) => (f.assigned_to ? f : { ...f, assigned_to: data[0].id }));
    } finally {
      setEmployeesLoaded(true);
    }
  };

  const load = async () => {
    const [t] = await Promise.all([api.get("/tasks"), loadEmployees()]);
    setTasks(t.data.map(withPhotoLinks));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = (isScheduled) => {
    setScheduling(isScheduled);
    setForm((f) => ({ ...f, scheduled_date: "" }));
    setOpen(true);
  };

  const create = async () => {
    if (!form.title || !form.customer_name || !form.assigned_to) {
      toast.error("Fill title, customer name and assignee");
      return;
    }
    if (scheduling && !form.scheduled_date) {
      toast.error("Pick the date this task should appear");
      return;
    }
    try {
      await api.post("/tasks", { ...form, scheduled_date: scheduling ? form.scheduled_date : null });
      toast.success(scheduling ? "Task scheduled" : "Task assigned");
      setOpen(false);
      setForm({ ...form, title: "", description: "", customer_name: "", customer_phone: "", customer_phone_2: "", scheduled_date: "" });
      await load();
    } catch (e) {
      toast.error("Failed to create task");
    }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || "",
      description: t.description || "",
      customer_name: t.customer_name || "",
      customer_phone: t.customer_phone || "",
      customer_phone_2: t.customer_phone_2 || "",
      assigned_to: t.assigned_to || "",
      priority: t.priority || "medium",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!form.title || !form.customer_name || !form.assigned_to) {
      toast.error("Fill title, customer name and assignee");
      return;
    }
    try {
      const { data } = await api.put(`/tasks/${editing.id}`, form);
      toast.success(data?.reopened ? "Task reassigned and moved back to Pending" : "Task updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update task");
    }
  };

  const move = async (t, status) => {
    await api.patch(`/tasks/${t.id}`, { status });
    await load();
  };

  const reassign = async (t, empId) => {
    if (!empId || empId === t.assigned_to) return;
    const prevAssignee = t.assigned_to;
    setTasks((cur) => cur.map((x) => (x.id === t.id ? { ...x, assigned_to: empId } : x)));
    try {
      const { data } = await api.put(`/tasks/${t.id}`, {
        title: t.title,
        description: t.description || "",
        customer_name: t.customer_name,
        customer_phone: t.customer_phone,
        customer_phone_2: t.customer_phone_2 || "",
        assigned_to: empId,
        priority: t.priority || "medium",
        due_date: t.due_date || null,
      });
      toast.success(
        data?.reopened
          ? `Reassigned to ${empName(empId)} and moved back to Pending`
          : `Reassigned to ${empName(empId)}`
      );
      await load(); // status and history may have changed
    } catch (e) {
      setTasks((cur) => cur.map((x) => (x.id === t.id ? { ...x, assigned_to: prevAssignee } : x)));
      toast.error(e?.response?.data?.detail || "Could not reassign task");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete task");
    }
  };

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  const matchesQuery = (t) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const norm = (v) => (v || "").toString().toLowerCase();
    return (
      norm(t.title).includes(s) ||
      norm(t.description).includes(s) ||
      norm(t.customer_name).includes(s) ||
      norm(t.customer_phone).includes(s) ||
      norm(t.customer_phone_2).includes(s) ||
      norm(t.task_no).includes(s) ||
      norm(empName(t.assigned_to)).includes(s)
    );
  };

  const filteredTasks = filterTasksByDate(tasks, dateFilter).filter(matchesQuery);
  const visibleColumns =
    statusFilter === "all" ? COLUMNS : COLUMNS.filter((c) => c.key === statusFilter);

  // Flip one complaint inside a ticket task between open ↔ done. The parent
  // task's status is untouched — admins still move the whole ticket through
  // the kanban when they're ready to close it.
  const toggleComplaint = async (taskId, complaintId, nextStatus) => {
    try {
      const { data } = await api.patch(
        `/tasks/${taskId}/complaints/${complaintId}`,
        { status: nextStatus }
      );
      setTasks((cur) =>
        cur.map((t) =>
          t.id === taskId
            ? {
                ...t,
                ticket_complaints: (t.ticket_complaints || []).map((c) =>
                  c.id === complaintId ? data.complaint : c
                ),
              }
            : t
        )
      );
      toast.success(
        nextStatus === "done"
          ? `Marked resolved (${data.done_count}/${data.total})`
          : "Reopened"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update complaint.");
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px]" data-testid="owner-tasks">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Field ops
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">Work list</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Assign customer visits, calls and follow-ups to your team.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TaskDateFilter value={dateFilter} onChange={setDateFilter} testId="owner-tasks-date-filter" />
          <Button variant="outline" onClick={() => openCreate(true)} data-testid="task-schedule-new">
            <CalendarClock className="w-4 h-4 mr-1" /> Schedule Task
          </Button>
          <Button className="bg-[#0055FF] hover:bg-[#0040CC]" onClick={() => openCreate(false)} data-testid="task-new">
            <Plus className="w-4 h-4 mr-1" /> New task
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
            <DialogHeader>
              <DialogTitle>{scheduling ? "Schedule task" : "New task"}</DialogTitle>
              {scheduling && (
                <p className="text-xs text-slate-500">
                  Stays out of the work list for you and the assignee until the date below, then appears in Pending automatically.
                </p>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="task-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Customer name</Label>
                  <Input
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    data-testid="task-customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    data-testid="task-customer-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Mobile No. 2 <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  value={form.customer_phone_2}
                  onChange={(e) => setForm({ ...form, customer_phone_2: e.target.value })}
                  placeholder="Alternate contact number"
                  data-testid="task-customer-phone-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Assign to</Label>
                  <Select
                    value={form.assigned_to}
                    onValueChange={(v) => setForm({ ...form, assigned_to: v })}
                  >
                    <SelectTrigger data-testid="task-assignee">
                      <SelectValue placeholder="Choose employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} · {e.department}
                        </SelectItem>
                      ))}
                      {employees.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500">
                          {employeesLoaded ? "No employees yet. Add one under Employees." : "Loading employees…"}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={form.priority}
                    onValueChange={(v) => setForm({ ...form, priority: v })}
                  >
                    <SelectTrigger data-testid="task-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Customer problem / notes</Label>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe what the customer needs so the employee has context before calling…"
                  data-testid="task-description"
                />
              </div>
              {scheduling && (
                <div className="space-y-2">
                  <Label>Appear on date</Label>
                  <Input
                    type="date"
                    min={todayStr()}
                    value={form.scheduled_date}
                    onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                    data-testid="task-scheduled-date"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={create}
                className="bg-[#0055FF] hover:bg-[#0040CC]"
                data-testid="task-submit"
              >
                {scheduling ? "Schedule task" : "Assign task"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search for keywords here to find a task…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
          data-testid="owner-tasks-search"
        />
      </div>

      <div className="mb-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="h-auto flex-wrap justify-start" data-testid="owner-tasks-status-filter">
            {STATUS_FILTERS.map((f) => {
              const count =
                f.key === "all"
                  ? filteredTasks.length
                  : filteredTasks.filter((t) => t.status === f.key).length;
              return (
                <TabsTrigger
                  key={f.key}
                  value={f.key}
                  data-testid={`owner-tasks-status-tab-${f.key}`}
                >
                  {f.label}
                  <span className="mono text-[10px] text-slate-400 ml-1.5">{count}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <div
        className={`grid gap-4 ${
          visibleColumns.length > 1
            ? visibleColumns.length >= 5
              ? "md:grid-cols-3 xl:grid-cols-5"
              : "md:grid-cols-4"
            : "md:grid-cols-1 max-w-md"
        }`}
      >
        {visibleColumns.map((col) => (
          <Card key={col.key} className="border-slate-200 shadow-none">
            <CardHeader className="border-b flex-row items-center justify-between space-y-0 py-3">
              <CardTitle className="font-heading text-sm flex items-center gap-2">
                <span className={`mono text-[10px] uppercase px-2 py-1 rounded ${col.accent}`}>
                  {col.label}
                </span>
                <span className="text-slate-400 mono text-xs">
                  {filteredTasks.filter((t) => t.status === col.key).length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3 min-h-[300px]">
              {filteredTasks
                .filter((t) => t.status === col.key)
                .map((t) => (
                  <div
                    key={t.id}
                    className={`border rounded-md p-3 hoverable-card ${
                      t.priority === "high"
                        ? "border-red-300 border-l-4 border-l-red-500 bg-red-50/60"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`task-card-${t.id}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className={`font-medium text-sm flex items-center gap-1.5 flex-wrap ${
                        t.priority === "high" ? "text-red-900" : ""
                      }`}>
                        {t.task_no != null && (
                          <span
                            className="mono text-[10px] text-slate-400 shrink-0"
                            data-testid={`task-no-${t.id}`}
                          >
                            #{t.task_no}
                          </span>
                        )}
                        {t.source === "customer_ticket" && (
                          <span
                            className="mono text-[9px] px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 uppercase inline-flex items-center gap-0.5"
                            title="Raised by the customer"
                            data-testid={`task-ticket-badge-${t.id}`}
                          >
                            <LifeBuoy className="w-2.5 h-2.5" /> Customer ticket
                          </span>
                        )}
                        {t.priority === "high" && (
                          <span
                            className="mono text-[9px] px-1.5 py-0.5 rounded bg-red-600 text-white uppercase font-bold shadow-sm"
                            title="High priority"
                            data-testid={`task-priority-high-${t.id}`}
                          >
                            High
                          </span>
                        )}
                        <span>{t.title}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => openEdit(t)}
                          className="text-slate-400 hover:text-[#0055FF] hover:bg-blue-50 rounded p-1 -mt-0.5 transition-colors"
                          title="Edit task"
                          data-testid={`task-edit-${t.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              type="button"
                              className="text-slate-400 hover:text-red-600 hover:bg-red-50 rounded p-1 -mr-1 -mt-0.5 transition-colors"
                              title="Delete task"
                              data-testid={`task-delete-${t.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                Delete this task?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove &ldquo;
                                <span className="font-medium text-slate-800">{t.title}</span>
                                &rdquo; for{" "}
                                <span className="mono text-slate-800">{t.customer_name}</span>.
                                The employee ({empName(t.assigned_to)}) will no longer
                                see it. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`task-delete-cancel-${t.id}`}>
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => remove(t.id)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid={`task-delete-confirm-${t.id}`}
                              >
                                Yes, delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    {t.description && (
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {t.description}
                      </div>
                    )}
                    {t.source === "customer_ticket" && t.ticket_order && (
                      <div
                        className="mono text-[10px] mt-2 inline-block bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded"
                        data-testid={`task-ticket-order-${t.id}`}
                      >
                        Order #{String(t.ticket_order.id || "").slice(0, 8)} · ₹{Number(t.ticket_order.total || 0).toLocaleString("en-IN")}
                      </div>
                    )}
                    {t.source === "customer_ticket" && Array.isArray(t.ticket_complaints) && t.ticket_complaints.length > 0 ? (
                      <div
                        className="mt-2 space-y-1.5 border-t border-slate-100 pt-2"
                        data-testid={`task-ticket-complaints-${t.id}`}
                      >
                        {t.ticket_complaints.length > 1 && (
                          <div className="mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center justify-between">
                            <span>Complaints</span>
                            <span>
                              {t.ticket_complaints.filter((c) => c.status === "done").length}/{t.ticket_complaints.length} resolved
                            </span>
                          </div>
                        )}
                        {t.ticket_complaints.map((c, idx) => {
                          const done = c.status === "done";
                          return (
                            <div
                              key={c.id || idx}
                              className={
                                "rounded border p-1.5 text-xs " +
                                (done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40")
                              }
                              data-testid={`task-ticket-complaint-${t.id}-${idx}`}
                            >
                              <div className="flex items-start gap-2">
                                <button
                                  type="button"
                                  onClick={() => toggleComplaint(t.id, c.id, done ? "open" : "done")}
                                  className={
                                    "shrink-0 mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center transition " +
                                    (done
                                      ? "bg-emerald-500 border-emerald-500 text-white"
                                      : "border-slate-300 hover:border-emerald-400")
                                  }
                                  title={done ? "Reopen" : "Mark resolved"}
                                  data-testid={`task-ticket-complaint-toggle-${t.id}-${idx}`}
                                >
                                  {done && <CheckSquare className="w-3 h-3" />}
                                </button>
                                <span
                                  className={
                                    "flex-1 " +
                                    (done ? "line-through text-slate-400" : "text-slate-700")
                                  }
                                >
                                  {t.ticket_complaints.length > 1 && (
                                    <b className="mono text-[10px] text-slate-500 mr-1">#{idx + 1}</b>
                                  )}
                                  {c.description}
                                </span>
                              </div>
                              {c.attachments && c.attachments.length > 0 && (
                                <div className="mt-1.5 grid grid-cols-3 gap-1 pl-6">
                                  {c.attachments.map((src, pIdx) => (
                                    <button
                                      key={pIdx}
                                      type="button"
                                      onClick={() =>
                                        setLightbox({
                                          images: c.attachments,
                                          startIndex: pIdx,
                                          title: `Complaint ${idx + 1}: ${t.customer_name || ""}`,
                                        })
                                      }
                                      className="aspect-square rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-fuchsia-400 transition"
                                      data-testid={`task-ticket-complaint-photo-${t.id}-${idx}-${pIdx}`}
                                      title="Tap to view full-size"
                                    >
                                      <img src={src} loading="lazy" decoding="async" alt={`Complaint ${idx + 1} photo ${pIdx + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : t.source === "customer_ticket" && (() => {
                      const imgs = (t.ticket_attachments && t.ticket_attachments.length)
                        ? t.ticket_attachments
                        : (t.ticket_attachment ? [t.ticket_attachment] : []);
                      if (!imgs.length) return null;
                      return (
                        <div
                          className="grid grid-cols-3 gap-1 mt-2"
                          data-testid={`task-ticket-photos-${t.id}`}
                        >
                          {imgs.map((src, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setLightbox({ images: imgs, startIndex: idx, title: `Ticket: ${t.customer_name || ""}` })}
                              className="aspect-square rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-fuchsia-400 transition"
                              data-testid={`task-ticket-photo-${t.id}-${idx}`}
                              title="Tap to view full-size"
                            >
                              <img src={src} loading="lazy" decoding="async" alt={`Ticket photo ${idx + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    {/* Employee proof-of-work photos — shown once task is done, and
                        kept (as earlier work) on a task reopened by reassigning. */}
                    {(t.status === "done" || t.reopened_at) && Array.isArray(t.proof_photos) && t.proof_photos.length > 0 && (
                      <div
                        className="grid grid-cols-3 gap-1 mt-2 border-t border-slate-100 pt-2"
                        data-testid={`task-proof-photos-${t.id}`}
                      >
                        <div className={`col-span-3 mono text-[9px] uppercase tracking-widest -mb-1 ${
                          t.status === "done" ? "text-emerald-700" : "text-slate-500"}`}>
                          {t.status === "done"
                            ? `Proof of work (${t.proof_photos.length})`
                            : `Earlier work (${t.proof_photos.length})${t.previous_done_at ? ` · done ${IST_STAMP(t.previous_done_at)}` : ""}`}
                        </div>
                        {t.proof_photos.map((src, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setLightbox({ images: t.proof_photos, startIndex: idx, title: `Proof: ${t.title}` })}
                            className="aspect-square rounded border border-emerald-200 overflow-hidden hover:ring-2 hover:ring-emerald-400 transition"
                            data-testid={`task-proof-photo-${t.id}-${idx}`}
                            title="Tap to view full-size"
                          >
                            <img src={src} loading="lazy" decoding="async" alt={`Proof photo ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    {t.status === "on_hold" && (
                      <div
                        className="mono text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block"
                        data-testid={`task-hold-reason-${t.id}`}
                      >
                        On hold: {t.hold_reason || "no reason given"}
                      </div>
                    )}
                    {(t.status === "done" || t.reopened_at) && t.done_comment && (
                      <div
                        className={`mono text-[10px] rounded px-2 py-1 mt-2 inline-block max-w-full whitespace-pre-wrap break-words border ${
                          t.status === "done"
                            ? "text-emerald-800 bg-emerald-50 border-emerald-200"
                            : "text-slate-600 bg-slate-50 border-slate-200"}`}
                        data-testid={`task-done-comment-${t.id}`}
                      >
                        {t.status === "done" ? "Outcome" : "Earlier outcome"}: {t.done_comment}
                      </div>
                    )}
                    {t.status === "scheduled" && t.scheduled_date && (
                      <div
                        className="mono text-[10px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1 mt-2 inline-block flex items-center gap-1 w-fit"
                        data-testid={`task-scheduled-date-${t.id}`}
                      >
                        <CalendarClock className="w-3 h-3" /> Appears {t.scheduled_date}
                      </div>
                    )}
                    {t.customer_name && (
                      <div
                        className="mt-3 flex items-center gap-1.5 text-[12px] font-medium text-slate-700"
                        data-testid={`task-customer-${t.id}`}
                      >
                        <User2 className="w-3.5 h-3.5 text-slate-400" /> {t.customer_name}
                      </div>
                    )}
                    <div className={`${t.customer_name ? "mt-1" : "mt-3"} flex items-center gap-2 mono text-[11px] text-slate-500`}>
                      <Phone className="w-3 h-3" /> {t.customer_phone}
                      <DialButton mode="exotel" taskId={t.id} number={t.customer_phone} size="xs" />
                    </div>
                    {t.customer_phone_2 && (
                      <div className="mt-1 flex items-center gap-2 mono text-[11px] text-slate-500">
                        <Phone className="w-3 h-3" /> {t.customer_phone_2}
                        <span className="text-[9px] uppercase tracking-wider text-slate-400">alt</span>
                        <DialButton number={t.customer_phone_2} size="xs" />
                        <CopyButton value={t.customer_phone_2} size="xs" />
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-1 mono text-[11px] text-slate-500">
                      <span>→</span>
                      <Select value={t.assigned_to} onValueChange={(v) => reassign(t, v)}>
                        <SelectTrigger
                          className="h-6 w-auto gap-1 border-none shadow-none px-1.5 mono text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:ring-0"
                          data-testid={`task-reassign-${t.id}`}
                        >
                          <SelectValue>{empName(t.assigned_to)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.name} · {e.department}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Audit trail — who + when for each status */}
                    <div className="mt-2 border-t border-slate-100 pt-2 space-y-0.5 mono text-[10px] text-slate-500">
                      <div className="flex items-center gap-1">
                        <User2 className="w-3 h-3" />
                        <span>Created by <b className="text-slate-700">{t.created_by_name || "—"}</b> · {IST_STAMP(t.created_at)}</span>
                      </div>
                      {(t.assignment_history || []).map((h, i) => (
                        <div key={i} className="flex items-start gap-1 text-violet-700" data-testid={`task-reassigned-${t.id}-${i}`}>
                          <span>↪</span>
                          <span>
                            {h.from_name || "—"} → <b>{h.to_name || "—"}</b> · {IST_STAMP(h.at)}
                            {h.by_name ? ` · by ${h.by_name}` : ""}
                            {h.reopened ? " · reopened" : ""}
                          </span>
                        </div>
                      ))}
                      {t.started_at && (
                        <div className="flex items-center gap-1 text-blue-700"
                          data-testid={`task-started-at-${t.id}`}>
                          <Play className="w-3 h-3" />
                          Started · {IST_STAMP(t.started_at)}
                        </div>
                      )}
                      {t.hold_at && (
                        <div className="flex items-center gap-1 text-amber-700"
                          data-testid={`task-hold-at-${t.id}`}>
                          <Pause className="w-3 h-3" />
                          On hold · {IST_STAMP(t.hold_at)}
                        </div>
                      )}
                      {t.done_at && (
                        <div className="flex items-center gap-1 text-emerald-700"
                          data-testid={`task-done-at-${t.id}`}>
                          <CheckCircle2 className="w-3 h-3" />
                          Done · {IST_STAMP(t.done_at)}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex gap-1 flex-wrap">
                      <button
                        onClick={() => setReassigning({ task: t, to: "" })}
                        className="mono text-[10px] uppercase px-2 py-1 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"
                        data-testid={`task-reassign-btn-${t.id}`}
                      >
                        <UserRoundCog className="w-3 h-3" /> Reassign
                      </button>
                      {MOVE_TARGETS.filter((c) => c.key !== col.key).map((c) => (
                        <button
                          key={c.key}
                          onClick={() => move(t, c.key)}
                          className="mono text-[10px] uppercase px-2 py-1 rounded border border-slate-200 hover:border-[#0055FF] hover:text-[#0055FF]"
                          data-testid={`task-move-${t.id}-${c.key}`}
                        >
                          → {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reassign: hand a task to someone else. A Done task reopens as Pending. */}
      <Dialog open={!!reassigning} onOpenChange={(o) => !o && setReassigning(null)}>
        <DialogContent data-testid="task-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign task</DialogTitle>
          </DialogHeader>
          {reassigning && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="mono text-slate-400">#{reassigning.task.task_no}</span>{" "}
                <span className="font-medium">{reassigning.task.title}</span>
                <div className="text-xs text-slate-500 mt-0.5">
                  Now with <b>{empName(reassigning.task.assigned_to)}</b>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Give it to</Label>
                <Select value={reassigning.to} onValueChange={(v) => setReassigning((r) => ({ ...r, to: v }))}>
                  <SelectTrigger data-testid="task-reassign-to">
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees
                      .filter((e) => e.id !== reassigning.task.assigned_to)
                      .map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} · {e.department}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {reassigning.task.status === "done" && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  This task is done. Reassigning moves it back to <b>Pending</b> as new work for them;
                  the earlier outcome and photos stay on the task.
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReassigning(null)}>Cancel</Button>
                <Button
                  className="bg-violet-600 hover:bg-violet-700"
                  disabled={!reassigning.to}
                  onClick={async () => {
                    const { task, to } = reassigning;
                    setReassigning(null);
                    await reassign(task, to);
                  }}
                  data-testid="task-reassign-confirm"
                >
                  Reassign
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="task-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="task-edit-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Customer name</Label>
                <Input
                  value={form.customer_name}
                  onChange={(e) =>
                    setForm({ ...form, customer_name: e.target.value })
                  }
                  data-testid="task-edit-customer-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={form.customer_phone}
                  onChange={(e) =>
                    setForm({ ...form, customer_phone: e.target.value })
                  }
                  data-testid="task-edit-customer-phone"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mobile No. 2 <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                value={form.customer_phone_2}
                onChange={(e) => setForm({ ...form, customer_phone_2: e.target.value })}
                placeholder="Alternate contact number"
                data-testid="task-edit-customer-phone-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Assign to</Label>
                <Select
                  value={form.assigned_to}
                  onValueChange={(v) => setForm({ ...form, assigned_to: v })}
                >
                  <SelectTrigger data-testid="task-edit-assignee">
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} · {e.department}
                      </SelectItem>
                    ))}
                    {employees.length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        {employeesLoaded ? "No employees yet. Add one under Employees." : "Loading employees…"}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v })}
                >
                  <SelectTrigger data-testid="task-edit-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Customer problem / notes</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                data-testid="task-edit-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              data-testid="task-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="task-edit-submit"
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.startIndex}
          title={lightbox.title}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
